import React, { useEffect, useMemo, useState } from "react";

import {
    Card,
    Empty,
    Spin,
    Tag,
    Typography
} from "antd";

import {
    BarChartOutlined,
    DollarOutlined,
    ReloadOutlined,
    WalletOutlined
} from "@ant-design/icons";

import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    ResponsiveContainer,
    Cell
} from "recharts";

import Swal from "sweetalert2";

import api from "../api/api";
import { useAcademicYear } from "../AcademicYearContext";

const { Title, Text } = Typography;

const ORANGE = "#ff7900";
const BLACK = "#111111";
const NAVY = "#062A4B";
const GOLD = "#F6C944";
const GREEN = "#16803C";
const PURPLE = "#7C3AED";

const CHART_COLORS = [
    ORANGE,
    NAVY,
    GREEN,
    GOLD,
    PURPLE,
    BLACK
];

const STUDENT_TYPES = [
    {
        value: "new",
        label: "New Student"
    },
    {
        value: "old",
        label: "Old Student"
    },
    {
        value: "scholarship",
        label: "Scholarship Student"
    },
    {
        value: "ward",
        label: "Ward Student"
    }
];

const SchoolFeeDataChart = () => {
    const { selectedYear } = useAcademicYear();

    const [gradeClasses, setGradeClasses] = useState([]);
    const [feesData, setFeesData] = useState([]);
    const [loading, setLoading] = useState(false);

    const normalizeType = (value) => {
        if (!value) {
            return "";
        }

        return String(value)
            .trim()
            .toLowerCase();
    };

    const getStudentTypeLabel = (type) => {
        const normalized = normalizeType(type);

        const found = STUDENT_TYPES.find(
            (item) => item.value === normalized
        );

        return (
            found?.label ||
            `${normalized || "Unknown"} Student`
        );
    };

    const getGradeClassId = (value) => {
        if (
            value &&
            typeof value === "object"
        ) {
            return value.id;
        }

        return value;
    };

    const getGradeClassName = (value) => {
        if (
            value &&
            typeof value === "object"
        ) {
            return value.name || "N/A";
        }

        const matched = gradeClasses.find(
            (item) =>
                Number(item.id) ===
                Number(value)
        );

        return matched?.name || "N/A";
    };

    const money = (value) => {
        const amount = Number(value || 0);

        return `$${amount.toLocaleString(
            undefined,
            {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            }
        )}`;
    };

    const compactMoney = (value) => {
        const amount = Number(value || 0);

        if (amount >= 1000000) {
            return `$${(
                amount / 1000000
            ).toFixed(1)}M`;
        }

        if (amount >= 1000) {
            return `$${(
                amount / 1000
            ).toFixed(1)}K`;
        }

        return `$${amount}`;
    };

    useEffect(() => {
        let mounted = true;

        const loadChartData = async () => {
            if (!selectedYear?.id) {
                setFeesData([]);
                setGradeClasses([]);
                return;
            }

            try {
                setLoading(true);

                const [
                    classResponse,
                    feesResponse
                ] = await Promise.all([
                    api.get(
                        "/api/users/grade-classes/"
                    ),

                    api.get(
                        "/api/users/school-fees-data/",
                        {
                            params: {
                                academic_year:
                                    selectedYear.id
                            }
                        }
                    )
                ]);

                if (!mounted) {
                    return;
                }

                const classes =
                    classResponse.data
                        ?.results ||
                    classResponse.data ||
                    [];

                const fees =
                    feesResponse.data
                        ?.results ||
                    feesResponse.data ||
                    [];

                const safeClasses =
                    Array.isArray(classes)
                        ? classes
                        : [];

                const safeFees =
                    Array.isArray(fees)
                        ? fees
                        : [];

                setGradeClasses(
                    safeClasses
                );

                const mappedFees =
                    safeFees.map(
                        (item) => {
                            const rawGradeClass =
                                item.grade_class;

                            const gradeClassId =
                                getGradeClassId(
                                    rawGradeClass
                                );

                            const matchedClass =
                                safeClasses.find(
                                    (grade) =>
                                        Number(
                                            grade.id
                                        ) ===
                                        Number(
                                            gradeClassId
                                        )
                                );

                            return {
                                ...item,
                                grade_class:
                                    matchedClass ||
                                    rawGradeClass
                            };
                        }
                    );

                setFeesData(
                    mappedFees
                );
            } catch (error) {
                console.error(
                    "School fee chart API error:",
                    error
                );

                if (mounted) {
                    setFeesData([]);

                    Swal.fire({
                        icon: "error",
                        title: "Chart Data Error",
                        text:
                            "Unable to load school fee chart data from the API.",
                        confirmButtonColor:
                            ORANGE
                    });
                }
            } finally {
                if (mounted) {
                    setLoading(false);
                }
            }
        };

        loadChartData();

        return () => {
            mounted = false;
        };
    }, [selectedYear]);

    const chartData = useMemo(() => {
        return feesData.map(
            (item, index) => {
                const gradeName =
                    getGradeClassName(
                        item.grade_class
                    );

                const studentType =
                    getStudentTypeLabel(
                        item.student_type
                    );

                const yearlyFee =
                    Number(
                        item.yearly_school_fee ||
                            0
                    );

                const registration =
                    Number(
                        item.registration ||
                            0
                    );

                const first =
                    Number(
                        item.first_installment ||
                            0
                    );

                const second =
                    Number(
                        item.second_installment ||
                            0
                    );

                const third =
                    Number(
                        item.third_installment ||
                            0
                    );

                const fourth =
                    Number(
                        item.fourth_installment ||
                            0
                    );

                return {
                    id:
                        item.id ||
                        `${index}-${gradeName}`,

                    name: gradeName,

                    shortName:
                        gradeName.length >
                        18
                            ? `${gradeName.substring(
                                  0,
                                  18
                              )}...`
                            : gradeName,

                    gradeClass:
                        gradeName,

                    studentType,

                    yearlyFee,

                    registration,

                    firstInstallment:
                        first,

                    secondInstallment:
                        second,

                    thirdInstallment:
                        third,

                    fourthInstallment:
                        fourth,

                    totalInstallments:
                        registration +
                        first +
                        second +
                        third +
                        fourth,

                    fill:
                        CHART_COLORS[
                            index %
                                CHART_COLORS.length
                        ]
                };
            }
        );
    }, [
        feesData,
        gradeClasses
    ]);

    const totals = useMemo(() => {
        return chartData.reduce(
            (result, item) => {
                result.yearly +=
                    item.yearlyFee;

                result.registration +=
                    item.registration;

                result.installments +=
                    item.totalInstallments;

                return result;
            },
            {
                yearly: 0,
                registration: 0,
                installments: 0
            }
        );
    }, [chartData]);

    const chartWidth = Math.max(
        760,
        chartData.length * 115
    );

    return (
        <Card
            className="school-fee-api-chart-card"
            bordered={false}
        >
            <div className="school-fee-chart-header">
                <div className="school-fee-chart-title-area">
                    <div className="school-fee-chart-icon">
                        <BarChartOutlined />
                    </div>

                    <div>
                        <span className="school-fee-chart-kicker">
                            FINANCIAL ANALYTICS
                        </span>

                        <Title
                            level={3}
                            className="school-fee-chart-title"
                        >
                            School Fee Analysis
                        </Title>

                        <Text className="school-fee-chart-subtitle">
                            Yearly fee structures loaded
                            directly from the active
                            academic year API.
                        </Text>
                    </div>
                </div>

                <div className="school-fee-chart-year">
                    <span>
                        ACTIVE YEAR
                    </span>

                    <strong>
                        {selectedYear?.name ||
                            "Not Selected"}
                    </strong>
                </div>
            </div>

            <div className="school-fee-chart-stat-grid">
                <div className="school-fee-chart-stat">
                    <div className="school-fee-chart-stat-icon orange">
                        <DollarOutlined />
                    </div>

                    <div>
                        <span>
                            TOTAL YEARLY FEES
                        </span>

                        <strong>
                            {money(
                                totals.yearly
                            )}
                        </strong>
                    </div>
                </div>

                <div className="school-fee-chart-stat">
                    <div className="school-fee-chart-stat-icon blue">
                        <WalletOutlined />
                    </div>

                    <div>
                        <span>
                            REGISTRATION
                        </span>

                        <strong>
                            {money(
                                totals.registration
                            )}
                        </strong>
                    </div>
                </div>

                <div className="school-fee-chart-stat">
                    <div className="school-fee-chart-stat-icon green">
                        <ReloadOutlined />
                    </div>

                    <div>
                        <span>
                            TOTAL SCHEDULED
                        </span>

                        <strong>
                            {money(
                                totals.installments
                            )}
                        </strong>
                    </div>
                </div>

                <div className="school-fee-chart-stat">
                    <div className="school-fee-chart-stat-icon dark">
                        <BarChartOutlined />
                    </div>

                    <div>
                        <span>
                            FEE STRUCTURES
                        </span>

                        <strong>
                            {chartData.length}
                        </strong>
                    </div>
                </div>
            </div>

            <div className="school-fee-chart-divider" />

            {loading ? (
                <div className="school-fee-chart-loading">
                    <Spin size="large" />

                    <Text>
                        Loading fee analytics...
                    </Text>
                </div>
            ) : !selectedYear ? (
                <div className="school-fee-chart-empty">
                    <Empty
                        description="Select an academic year to view fee analytics"
                    />
                </div>
            ) : chartData.length === 0 ? (
                <div className="school-fee-chart-empty">
                    <Empty
                        description={
                            `No school fee records found for ${
                                selectedYear.name ||
                                "the selected academic year"
                            }`
                        }
                    />
                </div>
            ) : (
                <div className="school-fee-chart-scroll">
                    <div
                        className="school-fee-chart-inner"
                        style={{
                            width: `${chartWidth}px`
                        }}
                    >
                        <ResponsiveContainer
                            width="100%"
                            height={430}
                        >
                            <BarChart
                                data={
                                    chartData
                                }
                                margin={{
                                    top: 25,
                                    right: 30,
                                    left: 10,
                                    bottom: 80
                                }}
                                barCategoryGap="22%"
                            >
                                <defs>
                                    <linearGradient
                                        id="schoolFeeBarGradient"
                                        x1="0"
                                        y1="0"
                                        x2="0"
                                        y2="1"
                                    >
                                        <stop
                                            offset="0%"
                                            stopColor={
                                                ORANGE
                                            }
                                        />

                                        <stop
                                            offset="100%"
                                            stopColor={
                                                "#d35400"
                                            }
                                        />
                                    </linearGradient>
                                </defs>

                                <CartesianGrid
                                    stroke="#e8edf3"
                                    strokeDasharray="4 5"
                                    vertical={false}
                                />

                                <XAxis
                                    dataKey="shortName"
                                    interval={0}
                                    angle={-32}
                                    textAnchor="end"
                                    height={90}
                                    tick={{
                                        fill: "#475569",
                                        fontSize: 12,
                                        fontWeight: 600
                                    }}
                                    axisLine={{
                                        stroke: "#dbe2ea"
                                    }}
                                    tickLine={false}
                                />

                                <YAxis
                                    tickFormatter={
                                        compactMoney
                                    }
                                    width={75}
                                    tick={{
                                        fill: "#64748b",
                                        fontSize: 11
                                    }}
                                    axisLine={false}
                                    tickLine={false}
                                />

                                <RechartsTooltip
                                    cursor={{
                                        fill: "rgba(255,121,0,0.06)"
                                    }}
                                    contentStyle={{
                                        borderRadius: 14,
                                        border:
                                            "1px solid #e2e8f0",
                                        boxShadow:
                                            "0 12px 30px rgba(15,23,42,0.12)",
                                        padding:
                                            "14px 16px"
                                    }}
                                    labelStyle={{
                                        color: BLACK,
                                        fontWeight: 800,
                                        marginBottom: 8
                                    }}
                                    formatter={(
                                        value
                                    ) => [
                                        money(
                                            value
                                        ),
                                        "Yearly Fee"
                                    ]}
                                    labelFormatter={(
                                        label,
                                        payload
                                    ) => {
                                        const item =
                                            payload?.[0]
                                                ?.payload;

                                        if (
                                            item
                                        ) {
                                            return `${item.gradeClass} • ${item.studentType}`;
                                        }

                                        return label;
                                    }}
                                />

                                <Bar
                                    dataKey="yearlyFee"
                                    name="Yearly Fee"
                                    radius={[
                                        10,
                                        10,
                                        0,
                                        0
                                    ]}
                                    maxBarSize={
                                        62
                                    }
                                >
                                    {chartData.map(
                                        (
                                            item,
                                            index
                                        ) => (
                                            <Cell
                                                key={
                                                    item.id
                                                }
                                                fill={
                                                    item.fill
                                                }
                                            />
                                        )
                                    )}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            )}

            {!loading &&
                chartData.length > 0 && (
                    <div className="school-fee-chart-footer">
                        <div>
                            <span className="chart-footer-dot orange" />

                            <span>
                                Yearly fee
                            </span>
                        </div>

                        <div>
                            <span className="chart-footer-dot navy" />

                            <span>
                                Data source: School Fees API
                            </span>
                        </div>

                        <Tag>
                            {chartData.length}{" "}
                            Structures
                        </Tag>
                    </div>
                )}

            <style>
                {`
                    .school-fee-api-chart-card {
                        width: 100%;
                        border-radius: 22px;
                        background: #ffffff;
                        box-shadow: 0 14px 45px rgba(15, 23, 42, 0.08);
                        overflow: hidden;
                    }

                    .school-fee-chart-header {
                        display: flex;
                        align-items: flex-start;
                        justify-content: space-between;
                        gap: 24px;
                    }

                    .school-fee-chart-title-area {
                        display: flex;
                        align-items: center;
                        gap: 16px;
                        min-width: 0;
                    }

                    .school-fee-chart-icon {
                        width: 54px;
                        height: 54px;
                        flex: 0 0 54px;
                        border-radius: 16px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        background: linear-gradient(
                            135deg,
                            #ff7900,
                            #d35400
                        );
                        color: #ffffff;
                        font-size: 24px;
                        box-shadow: 0 10px 24px rgba(255, 121, 0, 0.22);
                    }

                    .school-fee-chart-kicker {
                        display: block;
                        color: ${ORANGE};
                        font-size: 11px;
                        font-weight: 900;
                        letter-spacing: 1.4px;
                        margin-bottom: 4px;
                    }

                    .school-fee-chart-title {
                        margin: 0 !important;
                        color: ${BLACK} !important;
                        font-size: 23px !important;
                    }

                    .school-fee-chart-subtitle {
                        display: block;
                        color: #64748b;
                        margin-top: 4px;
                        line-height: 1.6;
                    }

                    .school-fee-chart-year {
                        min-width: 165px;
                        padding: 13px 16px;
                        border-radius: 14px;
                        background: #f8fafc;
                        border: 1px solid #e5eaf0;
                        text-align: right;
                    }

                    .school-fee-chart-year span {
                        display: block;
                        color: #94a3b8;
                        font-size: 10px;
                        font-weight: 900;
                        letter-spacing: 1px;
                        margin-bottom: 4px;
                    }

                    .school-fee-chart-year strong {
                        color: ${NAVY};
                        font-size: 15px;
                    }

                    .school-fee-chart-stat-grid {
                        display: grid;
                        grid-template-columns: repeat(4, minmax(0, 1fr));
                        gap: 14px;
                        margin-top: 26px;
                    }

                    .school-fee-chart-stat {
                        min-width: 0;
                        display: flex;
                        align-items: center;
                        gap: 12px;
                        padding: 15px;
                        border: 1px solid #e8edf3;
                        border-radius: 15px;
                        background: #fbfcfd;
                    }

                    .school-fee-chart-stat-icon {
                        width: 42px;
                        height: 42px;
                        flex: 0 0 42px;
                        border-radius: 12px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        font-size: 18px;
                    }

                    .school-fee-chart-stat-icon.orange {
                        color: ${ORANGE};
                        background: rgba(255, 121, 0, 0.12);
                    }

                    .school-fee-chart-stat-icon.blue {
                        color: ${NAVY};
                        background: rgba(6, 42, 75, 0.1);
                    }

                    .school-fee-chart-stat-icon.green {
                        color: ${GREEN};
                        background: rgba(22, 128, 60, 0.1);
                    }

                    .school-fee-chart-stat-icon.dark {
                        color: #ffffff;
                        background: ${BLACK};
                    }

                    .school-fee-chart-stat span {
                        display: block;
                        color: #94a3b8;
                        font-size: 9px;
                        font-weight: 900;
                        letter-spacing: 0.8px;
                        margin-bottom: 3px;
                    }

                    .school-fee-chart-stat strong {
                        display: block;
                        color: ${BLACK};
                        font-size: 17px;
                        font-weight: 900;
                        white-space: nowrap;
                        overflow: hidden;
                        text-overflow: ellipsis;
                    }

                    .school-fee-chart-divider {
                        height: 1px;
                        background: #edf1f5;
                        margin: 26px 0 10px;
                    }

                    .school-fee-chart-scroll {
                        width: 100%;
                        overflow-x: auto;
                        overflow-y: hidden;
                        padding-bottom: 8px;
                        -webkit-overflow-scrolling: touch;
                    }

                    .school-fee-chart-scroll::-webkit-scrollbar {
                        height: 7px;
                    }

                    .school-fee-chart-scroll::-webkit-scrollbar-track {
                        background: #f1f5f9;
                        border-radius: 20px;
                    }

                    .school-fee-chart-scroll::-webkit-scrollbar-thumb {
                        background: #cbd5e1;
                        border-radius: 20px;
                    }

                    .school-fee-chart-inner {
                        min-width: 760px;
                        height: 430px;
                    }

                    .school-fee-chart-loading,
                    .school-fee-chart-empty {
                        min-height: 430px;
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        justify-content: center;
                        gap: 16px;
                    }

                    .school-fee-chart-footer {
                        display: flex;
                        align-items: center;
                        justify-content: flex-end;
                        gap: 22px;
                        margin-top: 6px;
                        padding-top: 16px;
                        border-top: 1px solid #edf1f5;
                    }

                    .school-fee-chart-footer > div {
                        display: flex;
                        align-items: center;
                        gap: 7px;
                        color: #64748b;
                        font-size: 12px;
                        font-weight: 600;
                    }

                    .chart-footer-dot {
                        width: 9px;
                        height: 9px;
                        border-radius: 50%;
                    }

                    .chart-footer-dot.orange {
                        background: ${ORANGE};
                    }

                    .chart-footer-dot.navy {
                        background: ${NAVY};
                    }

                    @media (max-width: 992px) {
                        .school-fee-chart-stat-grid {
                            grid-template-columns: repeat(2, minmax(0, 1fr));
                        }

                        .school-fee-chart-header {
                            flex-direction: column;
                        }

                        .school-fee-chart-year {
                            width: 100%;
                            text-align: left;
                        }
                    }

                    @media (max-width: 600px) {
                        .school-fee-api-chart-card {
                            border-radius: 16px;
                        }

                        .school-fee-chart-header {
                            gap: 16px;
                        }

                        .school-fee-chart-title-area {
                            align-items: flex-start;
                        }

                        .school-fee-chart-icon {
                            width: 45px;
                            height: 45px;
                            flex-basis: 45px;
                            border-radius: 13px;
                            font-size: 19px;
                        }

                        .school-fee-chart-title {
                            font-size: 19px !important;
                        }

                        .school-fee-chart-subtitle {
                            font-size: 12px;
                        }

                        .school-fee-chart-stat-grid {
                            grid-template-columns: 1fr;
                            gap: 9px;
                        }

                        .school-fee-chart-stat {
                            padding: 12px;
                        }

                        .school-fee-chart-inner {
                            min-width: 680px;
                            height: 390px;
                        }

                        .school-fee-chart-loading,
                        .school-fee-chart-empty {
                            min-height: 320px;
                        }

                        .school-fee-chart-footer {
                            align-items: flex-start;
                            flex-direction: column;
                            gap: 9px;
                        }
                    }
                `}
            </style>
        </Card>
    );
};

export default SchoolFeeDataChart;